// src/components/organisms/Modal/Modal.tsx
import React, { Fragment, useEffect, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Button } from 'components/atoms/Button/Button';
import * as styles from './Modal.css';

type ResolveFn = (value?: any) => void;

function parseMessage(message: string): React.ReactNode {
  return message.split(/\r?\n/).map((line, i, arr) => (
    <Fragment key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </Fragment>
  ));
}

interface ModalOptions {
  title?:    string;
  message:   string;
  okText:    string;
  cancelText?: string;
}

function createModalContainer(): { container: HTMLDivElement; root: Root } {
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);
  return { container, root };
}

function destroy(container: HTMLDivElement, root: Root) {
  root.unmount();
  container.remove();
}

function ModalComponent({
  opts,
  resolve,
}: {
  opts: ModalOptions;
  resolve: ResolveFn;
}) {
  const { title, message, okText, cancelText } = opts;
  const [visible, setVisible] = useState(false);

  // 마운트 직후 visible=true → 애니메이션 트리거
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = (val?: any) => {
    // 닫기 애니메이션
    setVisible(false);
    // transition 후 resolve & 언마운트
    setTimeout(() => resolve(val), 200);
  };

  // 백드롭 클릭 시 닫기
  const onBackdropClick = () => handleClose(cancelText ? false : undefined);

  // 모달 내부 클릭은 전파 방지
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      className={[
        styles.backdrop,
        visible && styles.backdropVisible,
      ].filter(Boolean).join(' ')}
      onClick={onBackdropClick}        // ← 백드롭 클릭 핸들러
    >
      <div
        className={[
          styles.modal,
          visible && styles.modalVisible,
        ].filter(Boolean).join(' ')}
        onClick={stop}                 // ← 내부 클릭 전파 방지
      >
        <div className={styles.content}>
          {title && (
            <div className="mb-4 text-lg font-bold text-gray-900">
              {title}
            </div>
          )}
          {parseMessage(message)}
        </div>

        <div className={styles.footer}>
          {cancelText && (
            <Button
              className="w-full max-w-[280px]"
              intent="secondary"
              onClick={() => handleClose(false)}
            >
              {cancelText}
            </Button>
          )}
          <Button
            className="w-full max-w-[280px]"
            onClick={() => handleClose(cancelText ? true : undefined)}
          >
            {okText}
          </Button>
        </div>
      </div>
    </div>
  );
}

export const Modal = {
  alert(props: { title?: string; message: string; okText?: string }): Promise<void> {
    const { container, root } = createModalContainer();
    return new Promise<void>((resolve) => {
      root.render(
        <ModalComponent
          opts={{ 
            title: props.title, 
            message: props.message, 
            okText: props.okText || '확인' 
          }}
          resolve={() => {
            resolve();
            destroy(container, root);
          }}
        />
      );
    });
  },

  confirm(props: { 
    title?: string; 
    message: string; 
    okText?: string; 
    cancelText?: string; 
  }): Promise<boolean> {
    const { container, root } = createModalContainer();
    return new Promise<boolean>((resolve) => {
      root.render(
        <ModalComponent
          opts={{ 
            title: props.title, 
            message: props.message, 
            okText: props.okText || '확인',
            cancelText: props.cancelText || '취소'
          }}
          resolve={(val) => {
            resolve(val);
            destroy(container, root);
          }}
        />
      );
    });
  },
};